import React from 'react';
import { Box, Skeleton, SxProps, Theme } from '@mui/material';

/**
 * Dark loading placeholders for the Learning Dashboard (admin) and My Learning
 * Pathway (learner). They are shaped like the real layout, so when the data
 * arrives the content fades in where the placeholder was instead of popping in
 * piece by piece behind a spinner.
 */

const BONE_BG = '#232323';

/**
 * Content reveal once its placeholder is swapped out.
 *
 * This animation deliberately does NOT touch `transform`, and that matters
 * beyond style. With fill mode `both` the element keeps the animation's final
 * COMPUTED value after it ends.
 * The last keyframe says `transform: none`, but interpolating a transform to
 * `none` computes the identity matrix, so the element was left with
 * `transform: matrix(1, 0, 0, 1, 0, 0)` forever. A transform other than the
 * `none` keyword makes an element the containing block for every
 * `position: fixed` descendant, and react-beautiful-dnd positions the card you
 * are dragging with `position: fixed`. The dragged card was therefore placed
 * relative to this wrapper instead of the viewport and rendered ~200px below
 * the cursor on the learner dashboard (measured) and on the admin board, which
 * uses this same style around its droppable columns.
 *
 * The reveal is therefore opacity only. Switching the fill mode to `backwards`
 * also measures clean once the animation ends, but it still leaves a 300ms
 * window during the run in which the element is a containing block — a drag
 * started in that window would be displaced again. Animating no transform at
 * all closes that window, and it keeps this style safe for anyone who later
 * spreads it around a fixed-positioned descendant. The cost is a 4px rise
 * nobody could see.
 *
 * Do NOT try to neutralise it at the call site with `transform: 'none'` in the
 * Box's own sx: a filling animation beats a normal declaration in the cascade,
 * so the computed transform stays the identity matrix.
 */
export const fadeInSx = {
  animation: 'dosenFadeIn 0.3s ease both',
  '@keyframes dosenFadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 }
  }
} as const;

export const Bone: React.FC<{
  width?: number | string;
  height: number | string;
  radius?: number | string;
  sx?: SxProps<Theme>;
}> = ({ width = '100%', height, radius = '6px', sx }) => (
  <Skeleton
    variant="rounded"
    animation="wave"
    width={width}
    height={height}
    sx={[
      {
        flexShrink: 0,
        bgcolor: BONE_BG,
        borderRadius: radius,
        '&::after': { background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)' }
      },
      ...(Array.isArray(sx) ? sx : [sx])
    ]}
  />
);

/** One program card: title pill, progress ring and the owner / CTA row. */
export const ProgramCardSkeleton: React.FC<{ height: number }> = ({ height }) => (
  <Box
    sx={{
      height,
      flexShrink: 0,
      p: '10px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      bgcolor: '#1E1E1E',
      border: '1px solid #2A2A2A',
      borderRadius: '6px'
    }}
  >
    <Bone height={24} />
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Bone width={52} height={52} radius="50%" />
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
      <Bone width="45%" height={14} />
      <Bone width={72} height={26} />
    </Box>
  </Box>
);

/** The four framework columns (Onboard / Operate / Perform / Lead), each with a header and cards. */
export const FrameworkColumnsSkeleton: React.FC<{ cardHeight: number; columns?: number; cards?: number }> = ({
  cardHeight,
  columns = 4,
  cards = 3
}) => (
  <Box aria-busy aria-label="Loading" sx={{ flex: '1 0 100%', height: '100%', display: 'flex', overflow: 'hidden' }}>
    {Array.from({ length: columns }, (_, col) => (
      <Box
        key={col}
        sx={{
          flex: `0 0 ${100 / columns}%`,
          minWidth: 0,
          height: '100%',
          py: '14px',
          px: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          borderRight: '1px solid #2A2A2A',
          overflow: 'hidden'
        }}
      >
        <Bone height={34} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {Array.from({ length: cards }, (__, i) => (
            <ProgramCardSkeleton key={i} height={cardHeight} />
          ))}
        </Box>
      </Box>
    ))}
  </Box>
);

/** Team accordions in the Learning Gaps panel. */
export const GapsListSkeleton: React.FC<{ rows?: number }> = ({ rows = 4 }) => (
  <Box aria-busy aria-label="Loading" sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    {Array.from({ length: rows }, (_, i) => (
      <Bone key={i} height={30} />
    ))}
  </Box>
);

/** The admin dashboard's left panel, frame and all, before it knows which version to show. */
export const InsightsPanelSkeleton: React.FC<{ sx?: SxProps<Theme> }> = ({ sx }) => (
  <Box
    sx={[
      {
        p: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        border: '1px solid #2A2A2A',
        borderRadius: '8px'
      },
      ...(Array.isArray(sx) ? sx : [sx])
    ]}
  >
    <Bone width="60%" height={22} />
    <GapsListSkeleton />
  </Box>
);

/** Team Capability panel: the score card, the six month chart, then the capability rows. */
export const CapabilityPanelSkeleton: React.FC = () => (
  <Box
    aria-busy
    aria-label="Loading"
    data-testid="capability-panel-loading"
    sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
  >
    <Bone height={96} />
    <Bone height={112} />
    <Bone width="40%" height={20} />
    <GapsListSkeleton />
  </Box>
);

/** Learning Tier panel: the six month chart, then the five measure rows. */
export const TierPanelSkeleton: React.FC = () => (
  <Box aria-busy aria-label="Loading" sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
    <Bone height={112} />
    <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid #2A2A2A', borderRadius: '6px' }}>
      {Array.from({ length: 5 }, (_, i) => (
        <Box
          key={i}
          sx={{
            p: '14px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            borderTop: i === 0 ? 'none' : '1px solid #2A2A2A'
          }}
        >
          <Bone width="55%" height={14} />
          <Bone height={6} radius="3px" />
        </Box>
      ))}
    </Box>
  </Box>
);
